import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { LoadingService } from '../../../core/services/loading.service';
import { ItineraryTemplatesService } from '../../../core/services/itinerary-templates.service';
import { CurrencyService } from '../../../core/services/currency.service';
import { LanguageService } from '../../../core/services/language.service';
import { SettingService } from '../../../core/services/setting.service';
import { CategoryService } from '../../../core/services/category.service';
import { TagService } from '../../../core/services/tag.service';
import { AuthService } from '../../../modules/auth/services/auth.service';
import { TranslocoService } from '@ngneat/transloco';
import { AppUtils } from '../../../core/config/app.utils';
import { SETTINGS } from '../../../core/config/common.settings';
import * as moment from 'moment';

@Component({
  selector: 'app-add-itinerary-template',
  templateUrl: './add-itinerary-template.component.html',
  styleUrls: ['./add-itinerary-template.component.scss'],
})
export class AddItineraryTemplateComponent implements OnInit {
  cardLoading = true;
  tourForm!: FormGroup;
  formErrors: any = {
    type: {},
    currency: {},
    noOfDays: {},
    language: {},
    secondaryCurrency: {},
    exchangeRate: {},
    pax: {
      adults: {},
      children: {},
      infants: {},
    },
  };

  currencies: any[] = [];
  languages: any[] = [];
  selectedLanguages: any[] = [];
  activeCategories: any[] = [];
  activeTags: any[] = [];
  brand: any;

  isEditMode = false;
  templateId: string | null = null;
  existingTemplate: any = null;

  formatter = (value: number): string => `${value}`;
  parser = (value: string): string => value.replace(/\$\s?|(,*)/g, '');

  protected readonly SETTINGS = SETTINGS;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private message: NzMessageService,
    private loadingService: LoadingService,
    private itineraryTemplatesService: ItineraryTemplatesService,
    private currencyService: CurrencyService,
    private languageService: LanguageService,
    private settingService: SettingService,
    private categoryService: CategoryService,
    private tagService: TagService,
    private authService: AuthService,
    private translocoService: TranslocoService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.buildForm();
    await Promise.all([
      this.getCurrencies(),
      this.getLanguages(),
      this.findBrand(),
      this.getActiveCategories(),
    ]);
    const iti = this.route.snapshot.paramMap.get('iti');
    if (iti) {
      this.isEditMode = true;
      this.templateId = iti;
      await this.loadTemplate(iti);
    }
    this.cardLoading = false;
  }

  private buildForm(): void {
    this.tourForm = this.fb.group({
      type: ['B2C', [Validators.required]],
      category: [''],
      tags: [[]],
      currency: ['', [Validators.required]],
      secondaryCurrency: [''],
      exchangeRate: [''],
      noOfDays: [null, [Validators.required]],
      language: ['', [Validators.required]],
      season: [null],
      pax: this.fb.group({
        adults: ['1', [Validators.required]],
        children: ['0', [Validators.required]],
        infants: ['0', [Validators.required]],
      }),
    });

    this.tourForm.valueChanges.subscribe(() => {
      AppUtils.getFormErrors(this.tourForm, this.formErrors);
    });

    this.tourForm.get('secondaryCurrency')?.valueChanges.subscribe((value: any) => {
      const exchangeRateControl = this.tourForm.get('exchangeRate');
      if (value) {
        exchangeRateControl?.addValidators([Validators.required, Validators.min(0)]);
      } else {
        exchangeRateControl?.clearValidators();
        exchangeRateControl?.setValue('');
      }
      exchangeRateControl?.updateValueAndValidity();
    });
  }

  async loadTemplate(id: string): Promise<void> {
    this.loadingService.start();
    try {
      this.existingTemplate = await this.itineraryTemplatesService.getItineraryById({ id });
      this.patchForm(this.existingTemplate);
    } catch (e) {
      this.loadingService.stop();
      this.message.error(this.translocoService.translate('itineraryTemplates.messages.failedToLoad'));
      this.router.navigate(['/admin/template-itinerary']);
      return;
    }
    this.loadingService.stop();
  }

  patchForm(template: any): void {
    this.tourForm.patchValue({
      type: template?.tour?.type,
      category: template?.tour?.category?._id || template?.tour?.category || '',
      tags: template?.tour?.tags || [],
      currency: template?.tour?.currency,
      secondaryCurrency: template?.tour?.secondaryCurrency || '',
      exchangeRate: template?.tour?.exchangeRate || '',
      noOfDays: template?.tour?.noOfDays,
      language: template?.tour?.language,
      season: template?.tour?.season?._id || template?.tour?.season || null,
      pax: template?.tour?.pax,
    });
  }

  async saveTemplate(): Promise<void> {
    if (this.isEditMode) {
      await this.updateTemplate();
    } else {
      await this.createTemplate();
    }
  }

  async createTemplate(): Promise<void> {
    if (this.tourForm.invalid) {
      AppUtils.getFormErrors(this.tourForm, this.formErrors);
      return;
    }

    this.loadingService.start();
    const { noOfDays, currency } = this.tourForm.getRawValue();
    const daysInfo = [];
    const experienceInfo = [];

    for (let i = 0; i < noOfDays; i++) {
      const date = moment().add(i, 'days');
      daysInfo.push({
        date: date.toDate(),
        overviewDescription: '',
        destination: null,
        remarksTips: '',
        transport: [],
      });
      experienceInfo.push({ date: date.toDate(), experiencesPerDay: [] });
    }

    const startDate = daysInfo[0]?.date;
    const endDate = daysInfo[daysInfo.length - 1]?.date;

    const { predefinedCosting } = this.brand || { predefinedCosting: [] };
    const additionalCosts = predefinedCosting
      ?.map(({ _id, ...rest }: any) => rest)
      ?.filter((preCost: any) => preCost.currency === currency || preCost.currency == null || preCost.currency === undefined) || [];

    const costing = { additionalCosts };
    const tourValues = this.tourForm.getRawValue();
    if (tourValues.season) {
      tourValues.season = this.getSeasonById(tourValues.season);
    }

    const payload: any = {
      tour: {
        ...tourValues,
        isDatesOptional: true,
        startDate,
        endDate,
        themeType: 'THEME_1',
      },
      itinerary: { inclusions: '', exclusions: '', policies: '' },
      title: '',
      days: daysInfo,
      accommodation: [],
      images: [],
      experiences: experienceInfo,
      costing,
    };

    // Clean empty references
    ['category', 'tags'].forEach((key) => {
      const v = payload.tour[key];
      if (v == null || v === '' || v === 'undefined' || v === undefined) {
        delete payload.tour[key];
      }
    });

    try {
      const result: any = await this.itineraryTemplatesService.createItinerary(payload);
      this.loadingService.stop();
      this.message.success(this.translocoService.translate('itineraryTemplates.messages.added'));
      this.router.navigate(['/admin/template-itinerary/edit', result._id]);
    } catch (e) {
      this.loadingService.stop();
      this.message.error(this.translocoService.translate('itineraryTemplates.messages.failedToAdd'));
    }
  }

  async updateTemplate(): Promise<void> {
    if (this.tourForm.invalid) {
      AppUtils.getFormErrors(this.tourForm, this.formErrors);
      return;
    }

    this.loadingService.start();
    const tourData = this.tourForm.getRawValue();
    if (tourData.season) {
      tourData.season = this.getSeasonById(tourData.season);
    }

    const existing = this.existingTemplate;
    const newNoOfDays = tourData.noOfDays;
    const daysInfo = [];
    const experienceInfo = [];

    for (let i = 0; i < newNoOfDays; i++) {
      const date = moment().add(i, 'days');
      const dayTransport = (existing.days[i]?.transport || []).map((item: any) => ({ ...item }));
      for (let t = 0; t < dayTransport.length; t++) {
        dayTransport[t].selectedDate = date.toDate();
        dayTransport[t].selectedDateIndex = i;
      }
      const day = existing.days[i];
      daysInfo.push({
        ...(day || {}),
        date: date.toDate(),
        destination: day?.destination?._id || day?.destination || null,
        transport: dayTransport,
      });
      experienceInfo.push({
        date: date.toDate(),
        experiencesPerDay: existing.experiences[i]?.experiencesPerDay || [],
      });
    }

    const startDate = daysInfo[0]?.date;
    const endDate = daysInfo[daysInfo.length - 1]?.date;

    const payload: any = {
      _id: existing._id,
      tour: {
        ...existing.tour,
        ...tourData,
        isDatesOptional: existing.tour?.isDatesOptional ?? true,
        startDate,
        endDate,
      },
      days: daysInfo,
      experiences: experienceInfo,
    };

    // Clean empty references
    ['category', 'tags'].forEach((key) => {
      const v = payload.tour[key];
      if (v == null || v === '' || v === 'undefined' || v === undefined) {
        delete payload.tour[key];
      }
    });

    try {
      await this.itineraryTemplatesService.updateItinerary(payload);
      this.loadingService.stop();
      this.message.success(this.translocoService.translate('itineraryTemplates.messages.updated'));
      await this.router.navigate(['/admin/template-itinerary/edit', existing._id]);
    } catch (e) {
      this.loadingService.stop();
      this.message.error(this.translocoService.translate('itineraryTemplates.messages.failedToUpdate'));
    }
  }

  routeBack(): void {
    this.router.navigate(['/admin/template-itinerary']);
  }

  async findBrand(): Promise<void> {
    const user = await this.authService.getLoggedInUser();
    const brand = await this.settingService.findBrand(user.userId);
    this.brand = brand;
    this.selectedLanguages = brand?.selectedLanguages || [];
  }

  get seasonCategory(): any[] {
    return this.brand?.seasonCategory || [];
  }

  getSeasonById(id: string): any {
    if (!id) return null;
    return this.seasonCategory.find((s: any) => s._id === id) || null;
  }

  async getLanguages(): Promise<void> {
    this.languages = await this.languageService.getContentLanguages();
  }

  getSelectedLanguagesObjectArray(): any[] {
    const arr: any[] = [];
    this.selectedLanguages?.forEach((lang: string) => {
      arr.push(this.languages?.find((l: any) => l.code === lang));
    });
    return arr.filter(Boolean);
  }

  async getCurrencies(): Promise<void> {
    this.currencies = await this.currencyService.getCurrencies();
  }

  async getActiveCategories(): Promise<void> {
    try {
      this.activeCategories = await this.categoryService.getAllActiveCategories();
    } catch (e: any) {
      this.message.error(e.error?.message);
    }
  }

  sortOrderCurrency(list: any[]): any[] {
    return list?.sort((a, b) => a.cc?.localeCompare(b.cc));
  }

  orderByName(list: any[]): any[] {
    return list?.sort((a, b) => a.name?.localeCompare(b.name));
  }
}
